// ============================================================================
// CacheStore - LRU Cache with TTL
// ============================================================================

class CacheStore {
    constructor(maxSize = 100, defaultTTL = 5 * 60 * 1000) {
        this.cache = new Map();
        this.maxSize = maxSize;
        this.defaultTTL = defaultTTL;
        this.accessOrder = new Set(); // Set maintains insertion order
        this.onEvict = null;
    }

    set(key, value, ttl = this.defaultTTL) {
        // Move to end of access order
        this.accessOrder.delete(key);
        this.accessOrder.add(key);

        this.cache.set(key, {
            value,
            expires: ttl ? Date.now() + ttl : null
        });

        // Evict oldest if over limit
        while (this.accessOrder.size > this.maxSize) {
            const oldest = this.accessOrder.values().next().value;
            this.accessOrder.delete(oldest);
            this.cache.delete(oldest);
            this.onEvict?.(oldest);
        }
    }

    get(key) {
        const item = this.cache.get(key);
        if (!item) return null;

        if (item.expires && Date.now() > item.expires) {
            this.delete(key);
            return null;
        }

        // Update access order
        this.accessOrder.delete(key);
        this.accessOrder.add(key);

        return item.value;
    }

    has(key) {
        const item = this.cache.get(key);
        if (!item) return false;
        if (item.expires && Date.now() > item.expires) {
            this.delete(key);
            return false;
        }
        return true;
    }

    delete(key) {
        this.cache.delete(key);
        this.accessOrder.delete(key);
    }

    clear() {
        this.cache.clear();
        this.accessOrder.clear();
    }

    size() {
        return this.cache.size;
    }
}

export { CacheStore };
